import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    signal,
    OnChanges,
    SimpleChanges,
    OnInit,
    inject,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { EInputFieldType } from '@app/core/enum/input-field.enum'
import {
    IInputFieldConfig,
    IInputFieldOption,
    TInputFieldTextFilter,
    TInputFieldValue,
} from '@app/features/school-site/shared/types/input-field.types'
import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import { USER_FIELD_VALIDATION } from '@app/features/school-site/constants/id-creation.constants'
import {
    EI_INPUT_FIELD_CONSTANTS,
    sanitizeContactPersonNameFragment,
    sanitizeToPositiveIntegerString,
    shouldAllowContactPersonNameKeyDown,
    shouldAllowPositiveIntegerKeyDown,
} from '../../constants/ei-input-field.constants'
import { SvgIconComponent } from '@app/shared/components/svg-icon/svg-icon.component'
import { calendarMonths, formatLocalDate } from '@/styles/components/calendar/calendar.utils'
import {
    getFieldMaxLengthErrorMessage,
    wouldExceedMaxLengthAfterInsert,
} from '@app/features/school-site/helpers/input-field.helper'

@Component({
    selector: 'ei-input-field',
    standalone: true,
    imports: [CommonModule, FormsModule, SvgIconComponent],
    templateUrl: './ei-input-field.component.html',
    styleUrls: ['./ei-input-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:click)': 'onDocumentClick($event)',
    },
})
export class EiInputFieldComponent implements OnInit, OnChanges {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef)

    @Input({ required: true }) config!: IInputFieldConfig
    @Input() value: TInputFieldValue = null
    @Input() countryValue = ''
    @Input() countryCodePrefix = ''
    @Input() alwaysShowLabel = false
    /** When provided and a date value is present, this string is shown overlaid on the native date input (e.g. "31 Mar 2026"). */
    @Input() dateDisplayText = ''

    @Output() valueChange = new EventEmitter<TInputFieldValue>()
    @Output() blur = new EventEmitter<void>()
    @Output() countryValueChange = new EventEmitter<string>()
    @Output() newOptionCreate = new EventEmitter<IInputFieldOption>()

    protected readonly fieldType = EInputFieldType
    protected readonly countryOptions: readonly IInputFieldOption[] = [
        { label: '🇮🇳 +91', value: 'in' },
    ]
    protected readonly regexPatterns = REGEX_PATTERNS
    protected readonly constants = EI_INPUT_FIELD_CONSTANTS

    protected isDropdownOpen = signal(false)
    protected isCountryDropdownOpen = signal(false)
    protected newOptionInput = signal('')
    protected availableOptions = signal<readonly IInputFieldOption[]>([])
    protected invalidTextMessage = signal<string | null>(null)

    ngOnInit(): void {
        // Initialize availableOptions from config when component initializes
        if (this.config?.options) {
            this.availableOptions.set(this.config.options)
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.config) {
            if (changes['config'] && this.config.options) {
                this.availableOptions.set(this.config.options)
            }
        }
    }

    protected get showLabel(): boolean {
        return this.hasValue(this.value)
    }

    protected get displayValue(): string {
        if (!this.value) return ''
        const selectedOption = this.availableOptions().find(
            (opt) => opt.value === String(this.value),
        )
        return selectedOption?.label || String(this.value)
    }

    protected get placeholderText(): string {
        return this.config.placeholder ?? ''
    }

    protected getPlaceholderParts(): { text: string; asterisk: string } {
        const placeholder = this.config.placeholder ?? ''
        return {
            text: placeholder,
            asterisk: '',
        }
    }

    protected onInputChange(event: Event): void {
        const target = event.target as HTMLInputElement
        let next = target.value
        let filterMessage: string | null = null

        if (this.config.type === EInputFieldType.Text && this.config.textFilter) {
            const filter = this.config.textFilter
            const prev = next
            const caret = target.selectionStart ?? prev.length
            next = this.applyTextFilter(prev, filter)
            if (next !== prev) {
                filterMessage = this.getInvalidMessage(filter)
                const beforeCaret = this.applyTextFilter(prev.slice(0, caret), filter)
                target.value = next
                const pos = Math.min(beforeCaret.length, next.length)
                setTimeout(() => target.setSelectionRange(pos, pos), 0)
            }
        }

        const beforeMaxLength = next
        next = this.applyMaxLength(next)
        if (next.length < beforeMaxLength.length) {
            this.invalidTextMessage.set(this.getMaxLengthErrorMessage())
        } else if (filterMessage) {
            this.invalidTextMessage.set(filterMessage)
        } else {
            this.invalidTextMessage.set(null)
        }

        if (next !== target.value) {
            target.value = next
        }
        this.valueChange.emit(next)
    }

    private getMaxLengthErrorMessage(): string {
        return getFieldMaxLengthErrorMessage(this.config.label, this.config.maxLength ?? 0)
    }

    private isNavigationOrShortcutKey(event: KeyboardEvent): boolean {
        const allowedKeys = this.constants.ALLOWED_PHONE_KEYS
        const keyLower = event.key.toLowerCase()
        const isCtrlA =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.SELECT_ALL &&
            (event.ctrlKey || event.metaKey)
        const isCtrlC =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.COPY && (event.ctrlKey || event.metaKey)
        const isCtrlV =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.PASTE && (event.ctrlKey || event.metaKey)
        const isCtrlX =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.CUT && (event.ctrlKey || event.metaKey)
        const isCtrlZ =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.UNDO && (event.ctrlKey || event.metaKey)

        return (
            allowedKeys.includes(event.key as (typeof allowedKeys)[number]) ||
            isCtrlA ||
            isCtrlC ||
            isCtrlV ||
            isCtrlX ||
            isCtrlZ
        )
    }

    private blockMaxLengthKeyDown(event: KeyboardEvent): boolean {
        if (this.config.maxLength == null) {
            return false
        }
        if (
            this.config.type !== EInputFieldType.Text &&
            this.config.type !== EInputFieldType.Email
        ) {
            return false
        }

        const target = event.target as HTMLInputElement
        const selectionStart = target.selectionStart ?? 0
        const selectionEnd = target.selectionEnd ?? 0
        if (
            !wouldExceedMaxLengthAfterInsert(
                target.value,
                selectionStart,
                selectionEnd,
                1,
                this.config.maxLength,
            )
        ) {
            return false
        }

        this.invalidTextMessage.set(this.getMaxLengthErrorMessage())
        event.preventDefault()
        return true
    }

    private applyMaxLength(value: string): string {
        if (
            this.config.maxLength == null ||
            (this.config.type !== EInputFieldType.Text &&
                this.config.type !== EInputFieldType.Email)
        ) {
            return value
        }
        return value.length > this.config.maxLength ? value.slice(0, this.config.maxLength) : value
    }

    private applyTextFilter(raw: string, filter: TInputFieldTextFilter): string {
        switch (filter) {
            case 'lettersOnly':
                return raw.replace(this.regexPatterns.NON_LETTER_CHARS_GLOBAL, '')
            case 'namePart':
                return raw.replace(this.regexPatterns.NON_NAME_PART_CHARS_GLOBAL, '')
            case 'digitsOnly':
                return raw.replace(this.regexPatterns.NON_NUMERIC, '')
            case 'section':
                return raw.replace(this.regexPatterns.NON_SECTION_CHARS_GLOBAL, '')
            case 'alphanumericOnly':
                return raw.replace(this.regexPatterns.NON_ALPHANUMERIC_CHARS_GLOBAL, '')
        }
    }

    private getInvalidMessage(filter: TInputFieldTextFilter): string {
        switch (filter) {
            case 'lettersOnly':
                return 'Enter alphabets only.'
            case 'namePart':
                return USER_FIELD_VALIDATION.messages.nameInvalidChars(this.config.label)
            case 'digitsOnly':
                return USER_FIELD_VALIDATION.messages.rollInvalidChars
            case 'section':
                return USER_FIELD_VALIDATION.messages.sectionInvalidChars
            case 'alphanumericOnly':
                return USER_FIELD_VALIDATION.messages.enrollmentInvalidChars
        }
    }

    protected onTextFilterKeyDown(event: KeyboardEvent): void {
        if (this.config.type !== EInputFieldType.Text) {
            return
        }

        if (this.isNavigationOrShortcutKey(event)) {
            return
        }

        if (event.key.length !== 1) {
            return
        }

        if (this.blockMaxLengthKeyDown(event)) {
            return
        }

        if (!this.config.textFilter) {
            this.invalidTextMessage.set(null)
            return
        }

        const filter = this.config.textFilter
        if (filter === 'digitsOnly') {
            if (!this.regexPatterns.SINGLE_DIGIT.test(event.key)) {
                this.invalidTextMessage.set(this.getInvalidMessage(filter))
                event.preventDefault()
            } else {
                this.invalidTextMessage.set(null)
            }
            return
        }
        if (filter === 'alphanumericOnly') {
            if (!this.regexPatterns.ALPHANUMERIC_SINGLE_KEY.test(event.key)) {
                this.invalidTextMessage.set(this.getInvalidMessage(filter))
                event.preventDefault()
            } else {
                this.invalidTextMessage.set(null)
            }
            return
        }
        if (filter === 'namePart') {
            if (!this.regexPatterns.NAME_PART_ALLOWED_SINGLE_KEY.test(event.key)) {
                this.invalidTextMessage.set(this.getInvalidMessage(filter))
                event.preventDefault()
            } else {
                this.invalidTextMessage.set(null)
            }
            return
        }
        if (filter === 'section') {
            if (!this.regexPatterns.SECTION_OTHER_ALLOWED_SINGLE_KEY.test(event.key)) {
                this.invalidTextMessage.set(this.getInvalidMessage(filter))
                event.preventDefault()
            } else {
                this.invalidTextMessage.set(null)
            }
            return
        }
        if (filter === 'lettersOnly') {
            if (!this.regexPatterns.LETTERS_ONLY_SINGLE_UNICODE_LETTER.test(event.key)) {
                this.invalidTextMessage.set(this.getInvalidMessage(filter))
                event.preventDefault()
            } else {
                this.invalidTextMessage.set(null)
            }
        }
    }

    protected onEmailKeyDown(event: KeyboardEvent): void {
        if (this.config.type !== EInputFieldType.Email) {
            return
        }

        if (this.isNavigationOrShortcutKey(event)) {
            return
        }

        if (event.key.length !== 1) {
            return
        }

        this.blockMaxLengthKeyDown(event)
    }

    protected onEmailPaste(event: ClipboardEvent): void {
        if (this.config.type !== EInputFieldType.Email || this.config.maxLength == null) {
            return
        }

        event.preventDefault()
        const pasted = event.clipboardData?.getData('text') ?? ''
        if (!pasted) {
            return
        }

        const target = event.target as HTMLInputElement
        const start = target.selectionStart ?? 0
        const end = target.selectionEnd ?? 0
        const current = target.value
        const merged = current.slice(0, start) + pasted + current.slice(end)
        const beforeMaxLength = merged
        const next = this.applyMaxLength(merged)
        if (next.length < beforeMaxLength.length) {
            this.invalidTextMessage.set(this.getMaxLengthErrorMessage())
        } else {
            this.invalidTextMessage.set(null)
        }
        target.value = next
        this.valueChange.emit(next)
        const pos = Math.min(start + pasted.length, next.length)
        setTimeout(() => target.setSelectionRange(pos, pos), 0)
    }

    protected onTextFilterPaste(event: ClipboardEvent): void {
        if (this.config.type !== EInputFieldType.Text || !this.config.textFilter) {
            return
        }
        event.preventDefault()
        const pasted = event.clipboardData?.getData('text') ?? ''
        const filtered = this.applyTextFilter(pasted, this.config.textFilter)
        if (!filtered) {
            return
        }
        const target = event.target as HTMLInputElement
        const start = target.selectionStart ?? 0
        const end = target.selectionEnd ?? 0
        const current = target.value
        const merged = current.slice(0, start) + filtered + current.slice(end)
        let next = this.applyTextFilter(merged, this.config.textFilter)
        const beforeMaxLength = next
        next = this.applyMaxLength(next)
        if (next.length < beforeMaxLength.length) {
            this.invalidTextMessage.set(this.getMaxLengthErrorMessage())
        } else if (filtered !== pasted) {
            this.invalidTextMessage.set(this.getInvalidMessage(this.config.textFilter))
        } else {
            this.invalidTextMessage.set(null)
        }
        target.value = next
        this.valueChange.emit(next)
        const pos = start + filtered.length
        setTimeout(() => {
            const endPos = Math.min(pos, next.length)
            target.setSelectionRange(endPos, endPos)
        }, 0)
    }

    protected onPositiveIntegerDigitsKeyDown(event: KeyboardEvent): void {
        if (!this.config.restrictToPositiveIntegerDigits) {
            return
        }
        if (!shouldAllowPositiveIntegerKeyDown(event)) {
            event.preventDefault()
            return
        }
        const maxDigits = this.config.maxDigits
        if (!maxDigits || !this.regexPatterns.SINGLE_DIGIT.test(event.key)) {
            return
        }
        const input = event.target as HTMLInputElement
        const selectionStart = input.selectionStart ?? 0
        const selectionEnd = input.selectionEnd ?? 0
        const currentValue = input.value || ''
        const selectionLength = selectionEnd - selectionStart
        const lengthAfterKey = currentValue.length - selectionLength + 1
        if (lengthAfterKey > maxDigits) {
            event.preventDefault()
        }
    }

    protected onPositiveIntegerDigitsPaste(event: ClipboardEvent): void {
        if (!this.config.restrictToPositiveIntegerDigits) {
            return
        }
        event.preventDefault()
        const pastedText = event.clipboardData?.getData('text') || ''
        const pastedDigits = sanitizeToPositiveIntegerString(pastedText)
        const target = event.target as HTMLInputElement
        const currentValue = target.value || ''
        const selectionStart = target.selectionStart || 0
        const selectionEnd = target.selectionEnd || 0
        const merged =
            currentValue.substring(0, selectionStart) +
            pastedDigits +
            currentValue.substring(selectionEnd)
        let normalized = sanitizeToPositiveIntegerString(merged)
        const maxDigits = this.config.maxDigits
        if (maxDigits !== undefined && normalized.length > maxDigits) {
            normalized = normalized.slice(0, maxDigits)
        }
        this.valueChange.emit(normalized)
        const caretAfterPaste = Math.min(selectionStart + pastedDigits.length, normalized.length)
        setTimeout(() => {
            target.setSelectionRange(caretAfterPaste, caretAfterPaste)
        }, 0)
    }

    protected onContactPersonNameKeyDown(event: KeyboardEvent): void {
        if (!this.config.restrictToContactPersonName) {
            return
        }
        if (!shouldAllowContactPersonNameKeyDown(event)) {
            event.preventDefault()
        }
    }

    protected onContactPersonNamePaste(event: ClipboardEvent): void {
        if (!this.config.restrictToContactPersonName) {
            return
        }
        event.preventDefault()
        const pastedText = event.clipboardData?.getData('text') || ''
        const pastedClean = sanitizeContactPersonNameFragment(pastedText)
        const target = event.target as HTMLInputElement
        const currentValue = target.value || ''
        const selectionStart = target.selectionStart || 0
        const selectionEnd = target.selectionEnd || 0
        const merged =
            currentValue.substring(0, selectionStart) +
            pastedClean +
            currentValue.substring(selectionEnd)
        const normalized = sanitizeContactPersonNameFragment(merged)
        this.valueChange.emit(normalized)
        const caretAfterPaste = Math.min(selectionStart + pastedClean.length, normalized.length)
        setTimeout(() => {
            target.setSelectionRange(caretAfterPaste, caretAfterPaste)
        }, 0)
    }

    protected onPhoneKeyDown(event: KeyboardEvent): void {
        // Allow navigation and control keys
        const allowedKeys = this.constants.ALLOWED_PHONE_KEYS

        // Allow Ctrl/Cmd + A, C, V, X, Z
        const keyLower = event.key.toLowerCase()
        const isCtrlA =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.SELECT_ALL &&
            (event.ctrlKey || event.metaKey)
        const isCtrlC =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.COPY && (event.ctrlKey || event.metaKey)
        const isCtrlV =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.PASTE && (event.ctrlKey || event.metaKey)
        const isCtrlX =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.CUT && (event.ctrlKey || event.metaKey)
        const isCtrlZ =
            keyLower === this.constants.KEYBOARD_SHORTCUTS.UNDO && (event.ctrlKey || event.metaKey)

        if (
            allowedKeys.includes(event.key as (typeof allowedKeys)[number]) ||
            isCtrlA ||
            isCtrlC ||
            isCtrlV ||
            isCtrlX ||
            isCtrlZ
        ) {
            return
        }

        // Only allow numeric digits (0-9)
        if (!this.regexPatterns.SINGLE_DIGIT.test(event.key)) {
            event.preventDefault()
        }
    }

    protected onPhonePaste(event: ClipboardEvent): void {
        event.preventDefault()
        const pastedText = event.clipboardData?.getData('text') || ''
        // Extract only digits from pasted content
        const digitsOnly = pastedText.replace(this.regexPatterns.NON_NUMERIC, '')

        if (digitsOnly) {
            const target = event.target as HTMLInputElement
            const currentValue = target.value || ''
            const selectionStart = target.selectionStart || 0
            const selectionEnd = target.selectionEnd || 0

            // Insert digits at cursor position
            const newValue =
                currentValue.substring(0, selectionStart) +
                digitsOnly +
                currentValue.substring(selectionEnd)

            // Emit the new value
            this.valueChange.emit(newValue)

            // Update cursor position
            setTimeout(() => {
                const newPosition = selectionStart + digitsOnly.length
                target.setSelectionRange(newPosition, newPosition)
            }, 0)
        }
    }

    protected onSelectChange(event: Event): void {
        const target = event.target as HTMLSelectElement
        this.valueChange.emit(target.value)
    }

    protected onOptionSelect(option: IInputFieldOption): void {
        this.valueChange.emit(option.value)
        this.isDropdownOpen.set(false)
    }

    protected onDropdownToggle(event?: Event): void {
        if (event) {
            event.stopPropagation()
            event.preventDefault()
        }
        if (this.config.type === EInputFieldType.Select && this.config.allowCreateNew) {
            const currentState = this.isDropdownOpen()
            this.isDropdownOpen.set(!currentState)
        }
    }

    protected onOutsideClick(): void {
        this.isDropdownOpen.set(false)
    }

    protected onNewOptionInput(event: Event): void {
        const target = event.target as HTMLInputElement
        this.newOptionInput.set(target.value)
    }

    protected onCreateNew(): void {
        const newLabel = this.newOptionInput().trim()
        if (!newLabel) return

        const newOption: IInputFieldOption = {
            label: newLabel,
            value: newLabel.toLowerCase().replace(this.regexPatterns.WHITESPACE, '-'),
        }

        // Add to available options immediately
        this.availableOptions.update((options) => [...options, newOption])

        // Emit event for parent component
        this.newOptionCreate.emit(newOption)

        // Select the new option
        this.valueChange.emit(newOption.value)

        // Clear input and close dropdown
        this.newOptionInput.set('')
        this.isDropdownOpen.set(false)
    }

    protected onDateChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value
        this.valueChange.emit(value ? new Date(value + 'T00:00:00') : null)
    }

    protected onCountryChange(event: Event): void {
        const target = event.target as HTMLSelectElement
        this.countryValueChange.emit(target.value)
    }

    protected toggleCountryDropdown(event?: Event): void {
        if (event) {
            event.stopPropagation()
            event.preventDefault()
        }
        this.isCountryDropdownOpen.set(!this.isCountryDropdownOpen())
    }

    protected selectCountry(value: string): void {
        this.countryValueChange.emit(value)
        this.isCountryDropdownOpen.set(false)
    }

    protected get selectedCountryLabel(): string {
        if (this.countryCodePrefix) {
            return this.countryCodePrefix
        }
        const selected =
            this.countryOptions.find((opt) => opt.value === this.countryValue) ??
            this.countryOptions[0]
        return selected?.label ?? ''
    }

    protected onDocumentClick(event: MouseEvent): void {
        const target = event.target as Node | null
        if (!target) return
        if (!this.elementRef.nativeElement.contains(target)) {
            this.isCountryDropdownOpen.set(false)
            this.isDropdownOpen.set(false)
        }
    }

    protected formatDateValue(value: TInputFieldValue): string {
        if (!value) return ''
        if (value instanceof Date) {
            if (isNaN(value.getTime())) return ''
            return formatLocalDate(value)
        }
        return String(value)
    }

    /** Shown in the field after picking a date (native control stays YYYY-MM-DD). */
    protected formatDateDisplayDdMmmYyyy(value: TInputFieldValue): string {
        if (!value) return ''
        if (value instanceof Date) {
            if (isNaN(value.getTime())) return ''
            const dayPadded = String(value.getDate()).padStart(2, '0')
            const monthLabel = calendarMonths[value.getMonth()]
            const year = value.getFullYear()
            return `${dayPadded}-${monthLabel}-${year}`
        }
        const trimmed = String(value).trim()
        const isoMatch = this.regexPatterns.ISO_DATE_YYYY_MM_DD.exec(trimmed)
        if (isoMatch) {
            const yearStr = isoMatch[1]
            const monthStr = isoMatch[2]
            const dayStr = isoMatch[3]
            const monthIndex = Number(monthStr) - 1
            if (monthIndex >= 0 && monthIndex < calendarMonths.length) {
                const monthLabel = calendarMonths[monthIndex]
                return `${dayStr}-${monthLabel}-${yearStr}`
            }
        }
        return trimmed
    }

    protected trackByOption(_: number, option: IInputFieldOption): string {
        return option.value
    }

    protected hasValue(value: TInputFieldValue): boolean {
        if (value === null || value === undefined) {
            return false
        }
        if (value instanceof Date) {
            return true
        }
        return String(value).trim().length > 0
    }
}
